import { useState, Fragment, useEffect, useCallback, useMemo } from "react";
import {
  CircularProgress,
  Grid2 as Grid,
  InputAdornment,
  Autocomplete,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { pdf } from "@react-pdf/renderer";
import BTInput from "../../components/BTInput/index.js";
import BTDialogBox from "../../components/BTDialgBox/index.js";
import BTBox from "../../components/BTBox/index.js";
import BTTypography from "../../components/BTTypography/index.js";
import EnhancedTable from "../../components/BTTable/index.js";
import moment from "moment";
import authService from "../../services/auth-service.js";
import InvoicePDF from "./reports/index.js";
import { useNotification } from "../../context/index.js";
import { Error, Notifications } from "@mui/icons-material";
import secureLocalStorage from "react-secure-storage";
import { batchUpdate } from "../../store/labSlice.js";
import { useDispatch } from "react-redux";
const Addbill = ({ open, setOpen, parties, todayRate, sizes, type }) => {
  const user = secureLocalStorage.getItem("userdata");
  const dispatch = useDispatch();
  const { billsData } = open || {};
  const [summaryData, setSummaryData] = useState(billsData);
  const [isConfirmed, setConfirm] = useState(false);
  const partyInfo = parties?.find(({ p_id }) => p_id === summaryData?.p_id);
  const [editableRate, setEditableRate] = useState(todayRate);
  const [selectedCompany, setCompany] = useState(
    partyInfo?.p_allias?.find(
      (company) => company?.p_org_name === billsData?.p_org_name
    )
  );
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotification();

  // Handle rate change and recalculate totals
  const handleRateChange = (newRate) => {
    setEditableRate(newRate);

    setSummaryData((prevData) => {
      if (!prevData?.sub_billing_lines) return prevData;

      const updatedLines = prevData.sub_billing_lines.map((line) => ({
        ...line,
        bill_total: line.bill_amount * Number(newRate),
      }));

      const bill_total = updatedLines.reduce(
        (total, item) => total + Number(item?.bill_total),
        0
      );

      return {
        ...prevData,
        sub_billing_lines: updatedLines,
        bill_total: bill_total?.toFixed(2),
      };
    });
  };

  // Handle table field changes with memoization
  const handleTableFieldChange = (name, value, index, row) => {
    if (name !== "bill_rate") return;

    setSummaryData((prevData) => {
      const newLines = [...prevData.sub_billing_lines];
      newLines[index] = {
        ...newLines[index],
        bill_rate: value,
        bill_amount:
          row.ser_price_type === 3
            ? row.bill_cts * Number(value)
            : row.bill_pcs * Number(value),
        bill_total: 0, // Will be updated in the next step
      };

      // Recalculate with current editableRate
      newLines[index].bill_total = newLines[index].bill_amount * editableRate;

      const bill_total = newLines.reduce(
        (total, item) => total + Number(item?.bill_total),
        0
      );

      const bill_amount = newLines.reduce(
        (sum, item) => sum + item.bill_amount,
        0
      );

      return {
        ...prevData,
        sub_billing_lines: newLines,
        bill_total: bill_total?.toFixed(2),
        bill_amount: bill_amount?.toFixed(2),
      };
    });
  };

  // Close handler
  const handleClose = useCallback(() => {
    setOpen(null);
  }, [setOpen]);

  // Download PDF handler
  const handleDownloadPDF = async () => {
    const blob = await pdf(
      <InvoicePDF data={summaryData} partyInfo={partyInfo} loginuser={user} />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    const newTab = window.open(url, "_blank");
    newTab.document.close();
    newTab.onbeforeunload = () => {
      URL.revokeObjectURL(url);
    };
  };

  // Fetch conversion rates

  // Initial data setup

  useEffect(() => {
    if (!selectedCompany) {
      setSummaryData({
        ...summaryData,
        p_org_name: partyInfo?.p_org_name,
        p_org_address_line1: partyInfo?.p_org_address_line1,
        p_org_address_line2: partyInfo?.p_org_address_line2,
        p_address: partyInfo?.p_address,
        p_org_pincode: partyInfo?.p_org_pincode,
        p_org_gst: partyInfo?.p_org_gst,
      });
    }
  }, [selectedCompany]);
  // Table columns with memoization
  const columns = [
    {
      id: "id",
      name: "ID",
      selector: (row, index) => index + 1,
      width: "50px",
    },
    { id: "service", name: "service", selector: (row) => row.ser_name },
    {
      id: "size",
      name: "size",
      selector: (row) => {
        const size = sizes?.find(({ size_id }) => size_id === row?.size_id);
        return size
          ? `${Number(size?.size_from)?.toFixed(2)} - ${Number(
              size?.size_to
            )?.toFixed(2)}`
          : "Fixed";
      },
      bodyCellStyle: {
        padding: "0px",
      },
    },
    {
      id: "pcs",
      name: "Pcs",
      selector: (row) => row.bill_pcs,
      bodyCellStyle: {
        padding: "0px",
      },
    },
    {
      id: "weight",
      name: "Weight",
      selector: (row) => row.bill_cts.toFixed(2),
      bodyCellStyle: {
        padding: "0px",
      },
    },
    {
      id: "rate",
      name: "Rate",
      selector: (row, index) => (
        <BTInput
          value={row?.bill_rate}
          variant="standard"
          onChange={(e) =>
            handleTableFieldChange("bill_rate", e.target.value, index, row)
          }
          sx={{
            position: "relative",
            top: "1.1px",
          }}
          type="number"
        />
      ),
      bodyCellStyle: {
        padding: "0px",
      },
    },
    {
      id: "amount",
      name: "Amount($)",
      selector: (row) => row?.bill_amount?.toFixed(2),
      bodyCellStyle: {
        padding: "0px",
      },
    },
    {
      id: "total",
      name: "Total",
      selector: (row) => row?.bill_total?.toFixed(2),
      bodyCellStyle: {
        padding: "0px",
      },
    },
  ];

  // Table styles with memoization
  const sub_count =
    summaryData?.sub_billing_lines?.reduce(
      (total, item) => total + (Number(item?.bill_pcs) || 0),
      0
    ) || 0;
  const sub_weight =
    summaryData?.sub_billing_lines?.reduce(
      (total, item) => total + (Number(item?.bill_cts) || 0),
      0
    ) || 0;

  // Submit handler
  const submitBill = async (e) => {
    e.preventDefault();

    const finalFormData = {
      ...summaryData,
      bill_pcs: +sub_count,
      bill_cts: +sub_weight?.toFixed(2),
      bill_total: summaryData?.sub_billing_lines?.reduce(
        (total, item) => total + Number(item?.bill_total),
        0
      ),
      bill_amount: summaryData?.sub_billing_lines?.reduce(
        (sum, item) => sum + item.bill_amount,
        0
      ),
      bill_status: isConfirmed ? 2 : 1,
      sub_billing_lines: summaryData?.sub_billing_lines?.map((item) => ({
        ...item,
        bill_status: isConfirmed ? 2 : 1,
        bill_date: summaryData?.bill_date,
        bill_invoice: summaryData?.bill_invoice,
      })),
    };
    try {
      setLoading(true);

      const response = await authService?.update_bill({
        attributes: finalFormData,
      });
      if (response?.status === 1) {
        const { sub_billing_lines, submits, ...data } = response?.data;
        const updateRec = type ? sub_billing_lines : [data];

        dispatch(
          batchUpdate({
            name: "bill",
            key: "bill_id",
            insert: [],
            updateItems: updateRec,
            removeItems: [],
          })
        );
        dispatch(
          batchUpdate({
            name: "submission",
            key: "stn_id",
            insert: [],
            updateItems: submits,
            removeItems: [],
          })
        );
        addNotification({
          title: "Successfully",
          message: response?.message,
          type: "success",
          color: "success",
          icon: <Notifications fontSize="small" color="white" />,
        });
        handleClose();
      }
    } catch (res) {
      addNotification({
        title: "#Error",
        message: res?.message || "Something went wrong",
        type: "error",
        color: "error",
        icon: <Error fontSize="small" color="white" />,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <BTDialogBox
      open={Boolean(open)}
      fullWidth
      maxWidth="xl"
      maxHeight="500px"
      minHeight="500px"
      onSubmit={submitBill}
      close={handleClose}
      title={"Edit"}
      buttons={[
        {
          name: "Cancel",
          color: "secondary",
          size: "small",
          onClick: handleClose,
        },
        {
          name: "Print",
          color: "dark",
          size: "small",
          onClick: handleDownloadPDF,
        },
        {
          name: isConfirmed ? "Confirm" : "Edit",
          variant: "gradient",
          type: "submit",
          color: "dark",
          size: "small",
          loading: loading,
          loadingComponent: (
            <Fragment>
              <svg width={0} height={0}>
                <defs>
                  <linearGradient
                    id="my_gradient"
                    x1="0%"
                    y1="0%"
                    x2="0%"
                    y2="100%">
                    <stop offset="0%" stopColor="#e01cd5" />
                    <stop offset="100%" stopColor="#1CB5E0" />
                  </linearGradient>
                </defs>
              </svg>
              <CircularProgress
                size={22}
                sx={{
                  "svg circle": { stroke: "url(#my_gradient)" },
                }}
              />
            </Fragment>
          ),
          sx: { ml: 2, width: "80px" },
          disabled: loading,
        },
      ]}>
      <Grid container spacing={1}>
        {/* Confirmation Switch */}
        {/* <Grid size={{ xs: 12 }}>
          <BTBox sx={{ display: "inline-flex" }}>
            <FormControlLabel
              labelPlacement="end"
              control={
                <Switch
                  value={isConfirmed}
                  size="small"
                  color="secondary"
                  onChange={(e) => {
                    setConfirm(e.target.checked);
                  }}
                />
              }
              label={"Confirmed"}
              sx={{
                width: "auto",
                m: 0,
                "& .MuiFormControlLabel-label": {
                  minWidth: "fit-content",
                },
              }}
            />
          </BTBox>
        </Grid> */}

        {/* Party Information Section */}
        <Grid size={{ xs: 12, md: 12 }}>
          <BTBox
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between", // Fixed the typo here
            }}>
            <BTBox
              mt={2}
              sx={{
                width: "100%", // Full width on mobile, auto on desktop
              }}>
              <FormControlLabel
                labelPlacement="end"
                control={
                  <Switch
                    value={isConfirmed}
                    size="small"
                    color="secondary"
                    onChange={(e) => {
                      setConfirm(e.target.checked);
                    }}
                  />
                }
                label={"Confirmed"}
                sx={{
                  width: "auto",
                  m: 0,
                  "& .MuiFormControlLabel-label": {
                    minWidth: "fit-content",
                  },
                }}
              />
            </BTBox>
            <BTBox
              sx={{
                width: "100%", // Full width on mobile, auto on desktop
              }}>
              <Autocomplete
                sx={{ width: "100%" }}
                options={partyInfo?.p_allias || []}
                getOptionLabel={(option) => option?.p_org_name || ""}
                value={selectedCompany}
                onChange={(_, newValue) => {
                  setCompany(newValue);
                  if (newValue) {
                    setSummaryData({
                      ...summaryData,
                      p_org_name: newValue.p_org_name,
                      p_org_address_line1: newValue.p_org_address_line1,
                      p_org_address_line2: newValue.p_org_address_line2,
                      p_address: partyInfo?.p_address,
                      p_org_pincode: partyInfo?.p_org_pincode,
                      p_org_gst: newValue.p_org_gst,
                    });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select company"
                    variant="standard"
                    sx={{ width: "100%" }}
                  />
                )}
              />
            </BTBox>
            <BTBox
              sx={{
                width: "100%", // Full width on mobile, fixed width on desktop
              }}>
              <BTTypography variant="caption" fontWeight="bold">
                Conversion Rate:
              </BTTypography>
              <BTInput
                value={editableRate}
                variant="standard"
                onChange={(e) => handleRateChange(e.target.value)}
                type="number"
                fullWidth
                size="small"
              />
            </BTBox>

            <BTBox
              sx={{
                width: "100%", // Full width on mobile, auto on desktop
              }}>
              <BTTypography variant="caption" fontWeight="bold">
                Invoice
              </BTTypography>
              <BTInput
                fullWidth
                size="small"
                variant="standard"
                value={summaryData?.bill_invoice}
                onChange={(e) =>
                  setSummaryData({
                    ...summaryData,
                    bill_invoice: e?.target.value,
                  })
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">#</InputAdornment>
                  ),
                }}
              />
            </BTBox>

            <BTBox
              sx={{
                width: "100%", // Full width on mobile, auto on desktop
              }}>
              <BTTypography variant="caption" fontWeight="bold">
                Date
              </BTTypography>
              <BTInput
                fullWidth
                size="small"
                type="datetime-local"
                variant="standard"
                value={moment(summaryData?.bill_date, "").format(
                  "YYYY-MM-DD HH:mm:ss"
                )}
                onChange={(e) =>
                  setSummaryData({
                    ...summaryData,
                    bill_date: moment(e?.target.value).format("YYYY-MM-DD"),
                  })
                }
              />
            </BTBox>
          </BTBox>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <BTBox
            sx={{
              p: 1,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              maxHeight: 300,
              overflow: "auto",
            }}>
            <BTTypography variant="subtitle2" gutterBottom>
              Party Information
            </BTTypography>

            <Grid container>
              <Grid size={{ xs: 12 }}>
                <Grid container>
                  <Grid size={{ xs: 5 }}>
                    <BTTypography variant="caption" fontWeight="bold">
                      Party Name:
                    </BTTypography>
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <BTTypography
                      variant="caption"
                      noWrap
                      title={partyInfo?.p_name}>
                      {partyInfo?.p_name}
                    </BTTypography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Grid container>
                  <Grid size={{ xs: 5 }}>
                    <BTTypography variant="caption" fontWeight="bold">
                      Party Address:
                    </BTTypography>
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <BTTypography
                      variant="caption"
                      noWrap
                      title={partyInfo?.p_address}>
                      {partyInfo?.p_address}
                    </BTTypography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Grid container>
                  <Grid size={{ xs: 5 }}>
                    <BTTypography variant="caption" fontWeight="bold">
                      Party Mo. No.:
                    </BTTypography>
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <BTTypography
                      variant="caption"
                      noWrap
                      title={partyInfo?.p_mo_no}>
                      {partyInfo?.p_mo_no}
                    </BTTypography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Grid container>
                  <Grid size={{ xs: 5 }}>
                    <BTTypography variant="caption" fontWeight="bold">
                      Party Email:
                    </BTTypography>
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <BTTypography
                      variant="caption"
                      noWrap
                      title={partyInfo?.p_email}>
                      {partyInfo?.p_email}
                    </BTTypography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Grid container>
                  <Grid size={{ xs: 5 }}>
                    <BTTypography variant="caption" fontWeight="bold">
                      Party GST #:
                    </BTTypography>
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <BTTypography
                      variant="caption"
                      noWrap
                      title={partyInfo?.p_org_gst}>
                      {partyInfo?.p_org_gst}
                    </BTTypography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </BTBox>
        </Grid>
        {/* Company Information Section */}
        <Grid size={{ xs: 12, md: 4 }}>
          <BTBox
            sx={{
              p: 1,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              maxHeight: 300,
              overflow: "auto",
            }}>
            <BTTypography variant="subtitle2" gutterBottom>
              Company Information
            </BTTypography>
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <Grid container>
                  <Grid size={{ xs: 5 }}>
                    <BTTypography variant="caption" fontWeight="bold">
                      Company Name:
                    </BTTypography>
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <BTTypography
                      variant="caption"
                      noWrap
                      title={summaryData?.p_org_name}>
                      {summaryData?.p_org_name}
                    </BTTypography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Grid container>
                  <Grid size={{ xs: 5 }}>
                    <BTTypography variant="caption" fontWeight="bold">
                      Address 1:
                    </BTTypography>
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <BTTypography
                      variant="caption"
                      noWrap
                      title={summaryData?.p_org_address_line1}>
                      {summaryData?.p_org_address_line1}
                    </BTTypography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Grid container>
                  <Grid size={{ xs: 5 }}>
                    <BTTypography variant="caption" fontWeight="bold">
                      Address 2:
                    </BTTypography>
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <BTTypography
                      variant="caption"
                      noWrap
                      title={summaryData?.p_org_address_line2}>
                      {summaryData?.p_org_address_line2}
                    </BTTypography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Grid container>
                  <Grid size={{ xs: 5 }}>
                    <BTTypography variant="caption" fontWeight="bold">
                      Pin code:
                    </BTTypography>
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <BTTypography
                      variant="caption"
                      noWrap
                      title={summaryData?.p_org_pincode}>
                      {summaryData?.p_org_pincode}
                    </BTTypography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Grid container>
                  <Grid size={{ xs: 5 }}>
                    <BTTypography variant="caption" fontWeight="bold">
                      Company GST#:
                    </BTTypography>
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <BTTypography
                      variant="caption"
                      noWrap
                      title={summaryData?.p_org_gst}>
                      {summaryData?.p_org_gst}
                    </BTTypography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </BTBox>
        </Grid>

        {/* Table Section */}
        <Grid size={{ xs: 12 }}>
          <BTBox
            sx={{
              width: "100%",
              overflow: "auto",
              maxHeight: 400,
            }}>
            <EnhancedTable
              columns={columns}
              rows={summaryData?.sub_billing_lines || []}
              tableHeader={false}
            />
          </BTBox>
        </Grid>
      </Grid>
    </BTDialogBox>
  );
};

export default Addbill;
